import { use, useEffect, useRef, useState } from 'react';
import { isVariantKnob, type ComponentSchema } from './schema/components';
import { useConstructor } from './store/useConstructor';
import { resolveKnob } from './store/resolveTokens';
import { ConstructorTemplateContext } from './store/ConstructorTemplate';
import { KnobControl } from './KnobControl';
import { VariantPicker } from './VariantPicker';

export interface ComponentSectionProps {
  schema: ComponentSchema;
  /**
   * True when the operator just clicked this component in the preview
   * iframe — drives auto-expand, smooth scroll into view, and the
   * accent-coloured selection border.
   */
  isSelected?: boolean;
  /** Variant carried with the selection (when click landed on a DS button
   *  rendered as e.g. `variant="accent-gloss"`). Auto-switches the local
   *  variant picker to it so the operator immediately sees the knobs of
   *  the kind of element they clicked. */
  selectedVariant?: string;
  /** Selection-tick — re-fires the scroll/highlight effect even when the
   *  operator re-clicks the same component (would otherwise be a no-op). */
  selectionNonce?: number;
}

/**
 * ComponentSection — collapsible section for a single DS-component. Header
 * shows name + group + variants count. Body has a swatch-preview row
 * (one coloured circle per color knob) and a stack of KnobControl rows.
 *
 * Selection sync: when `isSelected` transitions to `true` the section
 * auto-opens and the wrapper scrolls into view. We track which selection
 * value we already reacted to so a re-click on the *same* component still
 * triggers a fresh scroll (otherwise the second click is a no-op because
 * the section is already open).
 */
export function ComponentSection({
  schema,
  isSelected = false,
  selectedVariant,
  selectionNonce,
}: ComponentSectionProps) {
  const [open, setOpen] = useState(false);
  const { state } = useConstructor();
  const template = use(ConstructorTemplateContext);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // UI-only variant filter for per-variant components (no `variantKnob`).
  // When `variantKnob` is set the variant maps to a real CSS-var write and
  // we keep the current behaviour: show every knob, picker stays
  // store-bound. When it's unset, the picker becomes a local filter — the
  // body only renders knobs whose key starts with the chosen variant,
  // letting the operator theme one variant at a time without an explosion
  // of unrelated rows. Initial value = first variant (matches the picker's
  // own first-entry default so the editor opens in a consistent state).
  const isUiOnlyVariant =
    !!schema.variants && schema.variants.length > 0 && !schema.variantKnob;
  const [currentVariant, setCurrentVariant] = useState<string>(
    isUiOnlyVariant ? schema.variants![0]! : '',
  );

  useEffect(() => {
    if (!isSelected) return;
    setOpen(true);
    // Auto-switch the local variant picker to the variant the user
    // clicked, when the schema runs in UI-only variant mode (no
    // `variantKnob`) and the picked variant is one of the offered ones.
    // Without this guard a click on an `accent-gloss` button would leave
    // the picker on `primary` (variants[0]) and confuse the operator.
    if (
      isUiOnlyVariant &&
      selectedVariant &&
      schema.variants?.includes(selectedVariant)
    ) {
      setCurrentVariant(selectedVariant);
    }
    // Defer to next frame so the just-opened `<details>` body has its
    // final height and `scrollIntoView` lands on the expanded section,
    // not the collapsed header.
    const id = requestAnimationFrame(() => {
      wrapperRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
    return () => cancelAnimationFrame(id);
    // `selectionNonce` is part of deps so a re-click on the SAME
    // component still re-fires the effect (selecting + scrolling again).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, selectedVariant, selectionNonce]);

  // Filter knobs by the active variant when the picker runs UI-only
  // (match rule in `isVariantKnob`).
  const visibleKnobs = Object.entries(schema.knobs).filter(([key]) => {
    if (!isUiOnlyVariant || !currentVariant) return true;
    return isVariantKnob(key, currentVariant);
  });

  const colourKnobs = visibleKnobs.filter(([, k]) => k.kind === 'color');

  return (
    <div
      ref={wrapperRef}
      data-selected={isSelected || undefined}
      className={
        'rounded border transition-colors ' +
        (isSelected
          ? 'border-[color:var(--ui-accent,#22e11c)] bg-[color:color-mix(in_srgb,var(--ui-accent,#22e11c)_8%,transparent)]'
          : 'border-neutral-800')
      }
    >
      <button
        type="button"
        aria-label={`Toggle ${schema.name}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-neutral-900"
      >
        <div className="flex items-center gap-2">
          <span className="font-mono text-[13px] font-semibold text-white">
            {schema.displayName ?? schema.name}
          </span>
          <span className="rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide text-neutral-400">
            {schema.group}
          </span>
          {schema.variants && schema.variants.length > 0 && (
            <span className="font-mono text-[9px] text-neutral-500">
              {schema.variants.length} variants
            </span>
          )}
        </div>
        <span aria-hidden className="text-neutral-500">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div className="border-t border-neutral-800 px-3 py-3">
          {schema.variants && schema.variants.length > 0 && (
            <VariantPicker
              componentName={schema.name}
              variants={schema.variants}
              {...(schema.variantKnob ? { knobName: schema.variantKnob } : {})}
              label={schema.variantLabel ?? 'Variant'}
              // UI-only mode: when the schema declares no `variantKnob`,
              // wire `value`/`onChange` so the picker becomes a local
              // filter for `visibleKnobs` instead of writing the store.
              {...(isUiOnlyVariant
                ? { value: currentVariant, onChange: setCurrentVariant }
                : {})}
            />
          )}
          {colourKnobs.length > 0 && (
            <div className="mb-3 flex items-center gap-2 border-b border-neutral-800 pb-3">
              {colourKnobs.map(([knobName]) => {
                const colour = resolveKnob(state.value, schema.name, knobName, template) ?? '#000000';
                return (
                  <div
                    key={knobName}
                    data-testid="knob-swatch"
                    className="h-5 w-5 rounded-full border border-neutral-700"
                    style={{ backgroundColor: colour }}
                    title={`${knobName}: ${colour}`}
                  />
                );
              })}
            </div>
          )}

          <div className="flex flex-col gap-2">
            {visibleKnobs.map(([knobName, knob]) => (
              <div key={knobName} className="flex flex-col gap-1">
                <KnobControl
                  componentName={schema.name}
                  knobName={knobName}
                  knob={knob}
                />
                {/* Hint under the row, not inside the label — the label
                    column is a fixed `w-32 truncate`, so long labels get
                    cut off to "MOBILE BAR OFF…" with only a native
                    hover-title to recover the rest. */}
                {knob.hint && (
                  <p className="pl-1 text-[10px] leading-snug text-neutral-500">
                    {knob.hint}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
